import type { InstagramAccount } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import { mergeInstagramAccounts } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/mergeInstagramAccounts";

export type ListInstagramAccountsParams = {
  ad_account_id: string;
  facebook_page_id?: string;
};

export type ListInstagramAccountsFn = (
  params: ListInstagramAccountsParams
) => Promise<unknown>;

/**
 * Loads Instagram accounts for an ad account. When `facebookPageIds` is empty,
 * loads all accounts for the ad account; otherwise runs page-scoped fetches in
 * parallel and merges/dedupes results.
 */
export async function fetchInstagramAccountsForFacebookPages(
  adAccountId: string,
  facebookPageIds: string[],
  listInstagramAccounts: ListInstagramAccountsFn
): Promise<InstagramAccount[]> {
  const unique = Array.from(
    new Set(
      facebookPageIds.map((id) => String(id || "").trim()).filter(Boolean)
    )
  );

  if (unique.length === 0) {
    const r = await listInstagramAccounts({
      ad_account_id: adAccountId,
    });
    return (r as InstagramAccount[]) ?? [];
  }

  const settled = await Promise.allSettled(
    unique.map((facebook_page_id) =>
      listInstagramAccounts({
        ad_account_id: adAccountId,
        facebook_page_id,
      })
    )
  );

  const rows: InstagramAccount[] = [];
  for (const s of settled) {
    if (s.status === "fulfilled" && Array.isArray(s.value)) {
      rows.push(...(s.value as InstagramAccount[]));
    }
  }
  return mergeInstagramAccounts(rows);
}
